import { useEffect, useState } from "react";
import { Building2, Phone, Layers, Plus, Trash2, Pencil, Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { fmtDate } from "../../lib/format";

const STATES = ["Johor", "Kedah", "Kelantan", "Melaka", "Negeri Sembilan", "Pahang", "Perak", "Perlis",
  "Pulau Pinang", "Sabah", "Sarawak", "Selangor", "Terengganu", "WP Kuala Lumpur", "WP Labuan", "WP Putrajaya"];

function UnitsManager() {
  const [units, setUnits] = useState([]);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null); // {id, name}

  const load = () => api.get("/units").then((r) => setUnits(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newName.trim()) return;
    try {
      await api.post("/units", { name: newName });
      setNewName("");
      toast.success("Bahagian/Unit ditambah");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const rename = async () => {
    try {
      await api.put(`/units/${editing.id}`, { name: editing.name });
      setEditing(null);
      toast.success("Bahagian/Unit dikemas kini");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Padam "${u.name}"? Kenderaan & pengguna di bawahnya akan menjadi tiada bahagian.`)) return;
    try {
      await api.delete(`/units/${u.id}`);
      toast.success("Bahagian/Unit dipadam");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-3" data-testid="units-manager">
      <div className="flex gap-2">
        <Input data-testid="unit-name-input" placeholder="Cth: Bahagian Khidmat Pengurusan" value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} />
        <Button type="button" onClick={add} className="bg-[#10056E] hover:bg-[#2a17a8] shrink-0" data-testid="unit-add-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Tambah
        </Button>
      </div>
      <div className="space-y-2">
        {units.length === 0 && <p className="text-sm text-muted-foreground">Tiada Bahagian/Unit lagi.</p>}
        {units.map((u) => (
          <div key={u.id} className="flex items-center justify-between gap-2 border rounded-lg px-3 py-2" data-testid={`unit-row-${u.id}`}>
            {editing?.id === u.id ? (
              <Input className="h-8" value={editing.name} autoFocus
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), rename())} />
            ) : (
              <p className="text-sm font-medium flex items-center gap-2"><Layers className="h-3.5 w-3.5 text-[#10056E]" /> {u.name}</p>
            )}
            <div className="flex items-center gap-1 shrink-0">
              {editing?.id === u.id ? (
                <Button variant="outline" size="icon" className="h-7 w-7 text-[#198754]" onClick={rename} data-testid={`unit-save-${u.id}`}><Check className="h-3.5 w-3.5" /></Button>
              ) : (
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing({ id: u.id, name: u.name })} data-testid={`unit-edit-${u.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
              )}
              <Button variant="ghost" size="icon" className="h-7 w-7 text-[#DC3545]" onClick={() => remove(u)} data-testid={`unit-delete-${u.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AgencySettings({ tenant, reload }) {
  const s = tenant.settings || {};
  const [form, setForm] = useState({
    agency_name: tenant.name,
    agency_code: s.agency_code || "",
    agency_email: s.agency_email || "",
    agency_phone: s.agency_phone || "",
    agency_address: s.agency_address || "",
    agency_postcode: s.agency_postcode || "",
    agency_city: s.agency_city || "",
    agency_state: s.agency_state || "",
    agency_logo: s.agency_logo || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Sila muat naik fail imej"); return; }
    if (file.size > 1024 * 1024) { toast.error("Saiz logo melebihi 1MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, agency_logo: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success("Profil agensi disimpan");
      reload();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-base flex items-center gap-2"><Building2 className="h-4 w-4" /> Maklumat Agensi</CardTitle>
          <CardDescription>Maklumat rasmi agensi digunakan pada laporan CSV, emel notifikasi dan dokumen tempahan.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="h-20 w-20 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden bg-slate-50 shrink-0">
              {form.agency_logo ? (
                <img src={form.agency_logo} alt="Logo Agensi" className="h-full w-full object-contain" data-testid="agency-logo-preview" />
              ) : (
                <Building2 className="h-8 w-8 text-slate-300" />
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Logo Agensi</Label>
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#10056E] border border-[#10056E] rounded-lg px-3 py-1.5 cursor-pointer hover:bg-indigo-50">
                  <input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="agency-logo-input" />
                  Muat Naik Logo
                </label>
                {form.agency_logo && (
                  <Button type="button" variant="ghost" size="sm" className="text-[#DC3545] h-8"
                    onClick={() => setForm((f) => ({ ...f, agency_logo: "" }))} data-testid="agency-logo-remove">
                    Buang
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">PNG/JPG, maks 1MB. Dipaparkan pada Dashboard bersama maklumat agensi.</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Nama Agensi</Label>
              <Input data-testid="settings-agency-input" value={form.agency_name} onChange={set("agency_name")} />
            </div>
            <div className="space-y-1.5">
              <Label>Kod Agensi (pilihan)</Label>
              <Input data-testid="settings-agency-code-input" value={form.agency_code} onChange={set("agency_code")} placeholder="Cth: JPW-01" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Alamat</Label>
            <Textarea data-testid="settings-agency-address-input" value={form.agency_address} onChange={set("agency_address")}
              placeholder="Aras 3, Bangunan Persekutuan, Jalan..." />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label>Poskod</Label>
              <Input data-testid="settings-agency-postcode-input" value={form.agency_postcode} onChange={set("agency_postcode")} placeholder="62000" />
            </div>
            <div className="space-y-1.5">
              <Label>Bandar</Label>
              <Input data-testid="settings-agency-city-input" value={form.agency_city} onChange={set("agency_city")} placeholder="Putrajaya" />
            </div>
            <div className="space-y-1.5 col-span-2 sm:col-span-1">
              <Label>Negeri</Label>
              <Select value={form.agency_state} onValueChange={(v) => setForm({ ...form, agency_state: v })}>
                <SelectTrigger data-testid="settings-agency-state-select"><SelectValue placeholder="Pilih negeri" /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">
                  {STATES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-base flex items-center gap-2"><Phone className="h-4 w-4" /> Perhubungan Rasmi</CardTitle>
          <CardDescription>Digunakan sebagai maklumat balas pada emel dan laporan yang dijana sistem.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Emel Rasmi</Label>
              <Input data-testid="settings-agency-email-input" type="email" value={form.agency_email} onChange={set("agency_email")} placeholder="fleet@agensi.gov.my" />
            </div>
            <div className="space-y-1.5">
              <Label>No. Telefon Pejabat</Label>
              <Input data-testid="settings-agency-phone-input" value={form.agency_phone} onChange={set("agency_phone")} placeholder="03-8888 0000" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-base flex items-center gap-2"><Layers className="h-4 w-4" /> Bahagian / Unit</CardTitle>
          <CardDescription>Setiap kenderaan & pengguna ditugaskan kepada satu Bahagian/Unit — digunakan untuk keutamaan penugasan kenderaan.</CardDescription>
        </CardHeader>
        <CardContent>
          <UnitsManager />
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-base">Maklumat Sistem</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="border rounded-lg p-3">
              <p className="text-xs text-muted-foreground">ID Agensi (Tenant)</p>
              <p className="font-mono text-xs mt-1 break-all">{tenant.id}</p>
            </div>
            <div className="border rounded-lg p-3">
              <p className="text-xs text-muted-foreground">Didaftarkan Pada</p>
              <p className="font-semibold mt-1">{fmtDate(tenant.created_at)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Button onClick={save} disabled={saving} className="bg-[#10056E] hover:bg-[#2a17a8]" data-testid="settings-save-btn">
        {saving ? "Menyimpan..." : "Simpan Tetapan"}
      </Button>
    </div>
  );
}
